import { useEffect, useMemo, useState } from "react";
import { Download } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const when = (iso) => (iso ? new Date(iso).toLocaleString("en-CA", { timeZone: "America/Halifax", dateStyle: "medium", timeStyle: "short" }) : "");

const toCsv = (rows) => {
  const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
  const head = ["Email", "Phone", "Prize", "Code", "OK to text", "Date (Atlantic)"];
  return [head, ...rows.map((r) => [r.email, r.phone, r.prize, r.code, r.texts_ok ? "Yes" : "No", when(r.date)])].map((l) => l.map(esc).join(",")).join("\n");
};

const download = (rows) => {
  const url = URL.createObjectURL(new Blob([toCsv(rows)], { type: "text/csv" }));
  const a = Object.assign(document.createElement("a"), { href: url, download: "swolebears-mystery-bag-entries.csv" });
  a.click();
  URL.revokeObjectURL(url);
};

const Entry = ({ r }) => (
  <div data-testid="players-row" className="grid gap-1 border-t border-[#1B1530]/10 py-3 text-sm md:grid-cols-[2fr_1.2fr_1.2fr_1.4fr_0.7fr_1.4fr] md:items-center md:gap-3">
    <span className="font-bold break-all">{r.email}</span>
    <span>{r.phone || "-"}</span>
    <span className="font-semibold text-[#E8233F]">{r.prize}</span>
    <span className="font-mono text-xs break-all">{r.code || "-"}</span>
    <span className="text-[#4A4560]">{r.texts_ok ? "Texts OK" : "No texts"}</span>
    <span className="text-[#4A4560]">{when(r.date)}</span>
  </div>
);

// Owner-only list of mystery bag entries (email, phone, prize, code)
export const Players = ({ token }) => {
  const [rows, setRows] = useState(null);
  const [q, setQ] = useState("");
  useEffect(() => {
    fetch(`${API}/stats/players`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => (r.ok ? r.json() : []))
      .then((d) => setRows(Array.isArray(d) ? d : []))
      .catch(() => setRows([]));
  }, [token]);
  const shown = useMemo(() => (rows || []).filter((r) => `${r.email} ${r.phone} ${r.prize} ${r.code}`.toLowerCase().includes(q.toLowerCase())), [rows, q]);

  return (
    <section data-testid="players-section" className="mt-6 rounded-3xl bg-white p-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-display text-2xl">Mystery bag entries</h2>
          <p data-testid="players-count" className="text-sm text-[#4A4560]">{rows ? `${rows.length} ${rows.length === 1 ? "person" : "people"} entered their email` : "Loading..."}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <input data-testid="players-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search email, phone, prize"
            className="h-10 w-56 rounded-full border border-[#1B1530]/15 px-4 text-sm outline-none focus:border-[#FF5A5F]" />
          <button data-testid="players-download" onClick={() => download(shown)} disabled={!shown.length}
            className="flex h-10 items-center gap-2 rounded-full bg-[#1B1530] px-4 text-sm font-bold text-white transition-colors hover:bg-[#FF5A5F] disabled:opacity-40">
            <Download className="h-4 w-4" /> Download CSV
          </button>
        </div>
      </div>
      <div className="mt-4 hidden text-xs font-bold uppercase tracking-wider text-[#4A4560] md:grid md:grid-cols-[2fr_1.2fr_1.2fr_1.4fr_0.7fr_1.4fr] md:gap-3">
        <span>Email</span><span>Phone</span><span>Prize</span><span>Code</span><span>Texts</span><span>Date</span>
      </div>
      <div className="mt-2">
        {shown.map((r, i) => <Entry key={`${r.email}-${r.date}-${i}`} r={r} />)}
        {rows && !shown.length && <p data-testid="players-empty" className="py-6 text-sm text-[#4A4560]">No entries yet.</p>}
      </div>
    </section>
  );
};
